/**
 * Language mapping configuration
 * Maps language codes used in i18n to actual folder names in the locales directory
 */

export interface LanguageMapping {
	[key: string]: {
		folder: string;
		name: string;
		nativeName: string;
	};
}

export const languageMapping: LanguageMapping = {
	'en': {
		folder: 'en',
		name: 'English',
		nativeName: 'English'
	},
	'ar': {
		folder: 'ar-SA',
		name: 'Arabic',
		nativeName: 'العربية'
	},
	'de': {
		folder: 'de-DE',
		name: 'German',
		nativeName: 'Deutsch'
	},
	'es-LA': {
		folder: 'es-419',
		name: 'Spanish (Latin America)',
		nativeName: 'Español (Latinoamérica)'
	},
	'es': {
		folder: 'es-ES',
		name: 'Spanish',
		nativeName: 'Español'
	},
	'fr-CA': {
		folder: 'fr-CA',
		name: 'French (Canada)',
		nativeName: 'Français (Canada)'
	},
	'fr': {
		folder: 'fr-FR',
		name: 'French',
		nativeName: 'Français'
	},
	'he': {
		folder: 'he-IL',
		name: 'Hebrew',
		nativeName: 'עברית'
	},
	'id': {
		folder: 'id-ID',
		name: 'Indonesian',
		nativeName: 'Bahasa Indonesia'
	},
	'it': {
		folder: 'it-IT',
		name: 'Italian',
		nativeName: 'Italiano'
	},
	'ja': {
		folder: 'ja-JP',
		name: 'Japanese',
		nativeName: '日本語'
	},
	'ko': {
		folder: 'ko-KR',
		name: 'Korean',
		nativeName: '한국어'
	},
	'ms': {
		folder: 'ms-MY',
		name: 'Malay',
		nativeName: 'Bahasa Melayu'
	},
	'nl': {
		folder: 'nl-NL',
		name: 'Dutch',
		nativeName: 'Nederlands'
	},
	'no': {
		folder: 'no-NO',
		name: 'Norwegian',
		nativeName: 'Norsk'
	},
	'pl': {
		folder: 'pl-PL',
		name: 'Polish',
		nativeName: 'Polski'
	},
	'pt-BR': {
		folder: 'pt-BR',
		name: 'Portuguese (Brazil)',
		nativeName: 'Português (Brasil)'
	},
	'pt': {
		folder: 'pt-PT',
		name: 'Portuguese',
		nativeName: 'Português'
	},
	'ro': {
		folder: 'ro-RO',
		name: 'Romanian',
		nativeName: 'Română'
	},
	'ru': {
		folder: 'ru-RU',
		name: 'Russian',
		nativeName: 'Русский'
	},
	'sv': {
		folder: 'sv-SE',
		name: 'Swedish',
		nativeName: 'Svenska'
	},
	'tr': {
		folder: 'tr-TR',
		name: 'Turkish',
		nativeName: 'Türkçe'
	},
	'vi': {
		folder: 'vi-VN',
		name: 'Vietnamese',
		nativeName: 'Tiếng Việt'
	},
	'zh': {
		folder: 'zh-TW',
		name: 'Chinese (Traditional)',
		nativeName: '繁體中文'
	}
};

/**
 * Normalize an incoming language code to the canonical mapping key.
 * - Replaces underscores with dashes
 * - Lowercases the language part, uppercases alpha region (keeps numeric regions)
 * - Maps es-419 to es-la alias
 * - Falls back to base language if region-specific key not present
 */
export const resolveLanguageCode = (languageCode: string): string => {
	if (!languageCode) return languageCode;

	const cleaned = languageCode.replace(/_/g, '-');
	const parts = cleaned.split('-');

	let canonical = parts[0]?.toLowerCase() || '';
	if (parts[1]) {
		const region = parts[1];
		const upperRegion = /^[0-9]+$/.test(region) ? region : region.toUpperCase();
		canonical = `${canonical}-${upperRegion}`;
	}

	// Special alias mapping
	if (canonical.toLowerCase() === 'es-419') {
		return 'es-la';
	}

	if (languageMapping[canonical]) return canonical;

	const base = canonical.split('-')[0];
	if (languageMapping[base]) return base;

	return canonical;
};

/**
 * Get folder name for a language code
 */
export const getLanguageFolder = (languageCode: string): string => {
	const resolved = resolveLanguageCode(languageCode);
	return languageMapping[resolved]?.folder || resolved;
};

/**
 * Get all supported language codes
 */
export const getSupportedLanguages = (): string[] => {
	const keys = Object.keys(languageMapping);
	// Add underscore, lowercase aliases for regioned codes (e.g., fr-CA -> fr_ca)
	const underscoreAliases = keys
		.filter(k => k.includes('-'))
		.map(k => k.replace(/-/g, '_').toLowerCase());
	return Array.from(new Set([...keys, ...underscoreAliases]));
};
